import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, X, Sparkles, ImageOff, ChevronDown, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PER_PAGE = 50;
const DEFAULT_QUERY = 'nature';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Image search goes through the backend proxy at /api/search-images, which
// holds the Pixabay, Pexels and Unsplash credentials and falls back between
// them. The provider key used to be a literal here, which shipped it to every
// browser in the production bundle.
const IMAGE_SEARCH_URL = `${API_URL}/api/search-images`;


const MediaPanel = ({ onAddElement, onClose, onUploadStart, onUploadEnd, onPickImage }) => {
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages]           = useState([]);
  const [failedIds, setFailedIds]     = useState(new Set());
  const [isLoading, setIsLoading]     = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage]               = useState(1);
  const [totalHits, setTotalHits]     = useState(0);
  const [currentQuery, setCurrentQuery] = useState(DEFAULT_QUERY);
  const [noResults, setNoResults]     = useState(false);
  const [addingId, setAddingId]       = useState(null);

  const scrollRef   = useRef(null);
  const sentinelRef = useRef(null);

  // ─── Fetch ───────────────────────────────────────────────────────────────
  const fetchImages = useCallback(async (query, pageNum, append = false) => {
    if (append) setIsLoadingMore(true);
    else        setIsLoading(true);
    setNoResults(false);

    try {
      const params = new URLSearchParams({
        q:        query,
        per_page: PER_PAGE,
        page:     pageNum,
      });

      const res = await fetch(`${IMAGE_SEARCH_URL}?${params}`);
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Image search failed (${res.status})`);
      }

      const data = await res.json();
      setTotalHits(data.totalHits || 0);

      if (!data.hits || data.hits.length === 0) {
        if (!append) setNoResults(true);
        return;
      }

      // The proxy already normalises every provider to this shape, so the
      // panel no longer knows or cares which one answered.
      const mapped = data.hits.map((hit) => ({
        id:          hit.id,
        previewURL:  hit.previewURL,
        canvasURL:   hit.previewURL,
        alt:         hit.alt || 'photo',
        imageWidth:  hit.imageWidth  || 800,
        imageHeight: hit.imageHeight || 600,
        thumbWidth:  hit.thumbWidth  || 800,
        thumbHeight: hit.thumbHeight || 600,
        user:        hit.user || '',
      }));

      setImages((prev) => (append ? [...prev, ...mapped] : mapped));
      setPage(pageNum);
    } catch (err) {
      console.error('Image search error:', err);
      toast({
        title:       'Failed to load images',
        description: err.message || 'Check your connection and try again.',
        variant:     'destructive',
      });
      if (!append) setNoResults(true);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [toast]);

  // ─── On mount ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchImages(DEFAULT_QUERY, 1, false);
  }, [fetchImages]);

  // ─── Infinite scroll sentinel ─────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isLoading &&
          !isLoadingMore &&
          images.length < totalHits
        ) {
          fetchImages(currentQuery, page + 1, true);
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLoading, isLoadingMore, images.length, totalHits, currentQuery, page, fetchImages]);

  // ─── Search ───────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    const q = searchQuery.trim() || DEFAULT_QUERY;
    setCurrentQuery(q);
    setImages([]);
    setFailedIds(new Set());
    setPage(1);
    setTotalHits(0);
    fetchImages(q, 1, false);
  }, [searchQuery, fetchImages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // ─── Add to canvas ────────────────────────────────────────────────────
  const handleAddImage = async (image) => {
    if (addingId) return;
    setAddingId(image.id);
    onUploadStart?.();

    // STRICT: image MUST be stored locally — never fall back to Pixabay URL
    try {
      const res = await fetch(`${API_URL}/api/upload-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: image.canvasURL }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }

      const data = await res.json();
      if (!data.url) throw new Error('Server returned no URL');

      console.log('[MediaPanel] image stored locally:', data.url);
      // Replace mode: assign the stored image to the selected frame/mask/image
      // in place instead of adding a new canvas element.
      if (onPickImage && onPickImage(data.url)) {
        toast({ title: 'Image replaced' });
      } else {
        onAddElement({ type: 'image', src: data.url, x: 0, y: 0, width: image.imageWidth, height: image.imageHeight });
        toast({ title: 'Image added', description: 'Permanently stored — will never break.' });
      }
    } catch (err) {
      console.error('[MediaPanel] image storage failed:', err.message);
      toast({
        title: 'Image upload failed',
        description: err.message || 'Could not store image. Please try again.',
        variant: 'destructive',
      });
      // IMPORTANT: do NOT call onAddElement — no external URL enters the canvas
    } finally {
      onUploadEnd?.();
      setAddingId(null);
    }
  };

  // ─── Thumbnail error handling ─────────────────────────────────────────
  const handleThumbError = (id) => {
    setFailedIds((prev) => new Set([...prev, id]));
  };

  const visibleImages = images.filter((img) => !failedIds.has(img.id));
  const hasMore       = images.length < totalHits;

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-white select-none">

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm tracking-tight">Media Library</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-400 hover:text-gray-600"
              aria-label="Close media library"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search photos…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 transition-all placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={isLoading && !isLoadingMore}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
          >
            {isLoading && !isLoadingMore
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Search className="w-3.5 h-3.5" />}
            Search
          </button>
        </div>

        {/* Results meta */}
        {totalHits > 0 && !isLoading && (
          <p className="text-xs text-gray-400 mt-2 truncate">
            {totalHits.toLocaleString()} results for &ldquo;{currentQuery}&rdquo;
          </p>
        )}
      </div>

      {/* ── Gallery ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden" ref={scrollRef}>

        {/* Skeleton */}
        {isLoading && images.length === 0 && (
          <div className="p-3 grid grid-cols-2 gap-2">
            {Array.from({ length: 14 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl bg-gray-100 animate-pulse"
                style={{ height: [120, 100, 140, 110][i % 4] }}
              />
            ))}
          </div>
        )}

        {/* No results */}
        {!isLoading && noResults && (
          <div className="flex flex-col items-center justify-center h-52 gap-3 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <ImageOff className="w-6 h-6 text-gray-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">No photos found</p>
              <p className="text-xs text-gray-400 mt-0.5">Try a different keyword</p>
            </div>
            <button
              onClick={() => {
                setSearchQuery('');
                setCurrentQuery(DEFAULT_QUERY);
                setImages([]);
                setFailedIds(new Set());
                fetchImages(DEFAULT_QUERY, 1, false);
              }}
              className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-700 font-medium"
            >
              <RefreshCw className="w-3 h-3" /> Reset
            </button>
          </div>
        )}

        {/* Grid */}
        {visibleImages.length > 0 && (
          <div className="p-3">
            <div className="grid grid-cols-2 gap-2">
              {visibleImages.map((image, idx) => {
                const isAdding = addingId === image.id;
                // Use webformat aspect ratio for the thumbnail cell
                const ratio = image.thumbWidth && image.thumbHeight
                  ? `${image.thumbWidth}/${image.thumbHeight}`
                  : ['4/3', '1/1', '3/4', '16/9'][idx % 4];

                return (
                  <button
                    key={`${image.id}-${idx}`}
                    onClick={() => !isAdding && handleAddImage(image)}
                    disabled={isAdding}
                    title={image.alt}
                    draggable={!isAdding}
                    onDragStart={(e) => {
                      // External URL — the editor uploads it locally before
                      // assigning to a frame/mask (no external URL on canvas).
                      e.dataTransfer.setData('application/x-editor-media-src', image.canvasURL);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    className="group relative rounded-xl overflow-hidden border-2 border-transparent hover:border-violet-400 focus-visible:border-violet-500 focus-visible:outline-none transition-all duration-200 bg-gray-100 disabled:cursor-wait"
                    style={{ aspectRatio: ratio }}
                  >
                    {/* Thumbnail — webformatURL works without any CORS issues */}
                    <img
                      src={image.previewURL}
                      alt={image.alt}
                      loading="lazy"
                      decoding="async"
                      onError={() => handleThumbError(image.id)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-2">
                      <p className="text-white text-[10px] leading-tight line-clamp-2">{image.alt}</p>
                    </div>

                    {/* + badge */}
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <span className="text-gray-700 text-xs font-bold leading-none">+</span>
                    </div>

                    {/* Adding spinner */}
                    {isAdding && (
                      <div className="absolute inset-0 bg-white/75 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Infinite scroll sentinel — must be INSIDE the scrollable container */}
            <div ref={sentinelRef} className="h-6 mt-1" aria-hidden />

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-5 gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                <span className="text-xs text-gray-500">Loading more…</span>
              </div>
            )}

            {/* Manual fallback */}
            {hasMore && !isLoadingMore && (
              <div className="flex justify-center py-4">
                <button
                  onClick={() => fetchImages(currentQuery, page + 1, true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-200 text-xs text-gray-600 hover:border-violet-300 hover:text-violet-600 transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                  Load more
                </button>
              </div>
            )}

            {!hasMore && images.length > 0 && (
              <p className="text-center text-[10px] text-gray-400 py-3">
                All {totalHits.toLocaleString()} photos loaded
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/60 shrink-0">
        <p className="text-[10px] text-gray-400 text-center">
          Photos by{' '}
          <a
            href="https://pixabay.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-500 hover:text-violet-600 font-medium"
          >
            Pixabay
          </a>
        </p>
      </div>
    </div>
  );
};

export default MediaPanel;
